// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/compras/hooks/useBudget.ts
// ════════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { budgetService } from '../services/budgetService';
import type { SalvarCompraBudgetRequest } from '../types';

const KEY = 'compras-budget';

export const useBudgets = (ano?: number) =>
  useQuery({
    queryKey: [KEY, ano],
    queryFn:  () => budgetService.listar(ano),
  });

export const useBudgetVsRealizado = (
  ano: number,
  mesInicio?: number,
  mesFim?: number,
) =>
  useQuery({
    queryKey: [KEY, 'comparativo', ano, mesInicio, mesFim],
    queryFn:  () => budgetService.comparativo(ano, mesInicio, mesFim),
    enabled:  ano > 0,
  });

export const useSalvarBudget = () => {
  const qc = useQueryClient();

  const criar = useMutation({
    mutationFn: (req: SalvarCompraBudgetRequest) => budgetService.criar(req),
    onSuccess:  () => qc.invalidateQueries({ queryKey: [KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: (p: { id: number; req: SalvarCompraBudgetRequest }) =>
      budgetService.atualizar(p.id, p.req),
    onSuccess:  () => qc.invalidateQueries({ queryKey: [KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => budgetService.excluir(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: [KEY] }),
  });

  return {
    criar:       criar.mutateAsync,
    atualizar:   atualizar.mutateAsync,
    excluir:     excluir.mutateAsync,
    isSaving:    criar.isPending || atualizar.isPending,
    isDeleting:  excluir.isPending,
  };
};
